import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, ShieldCheckIcon } from 'lucide-react';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorNotice,
  Field,
  Panel,
  Pill,
  SearchInput,
  SkeletonTable,
  TextInput,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';
import { ROLE_QUERY_KEY, createRole, listRoles, type Role } from '@/lib/roles';

/** Sortable columns → the row field each orders by. */
const ROLE_SORT_FIELDS = { name: 'name', permissions: 'permissionCount', members: 'memberCount' } as const;
const ROLE_SORT_KEYS = Object.values(ROLE_SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: ROLE_SORT_FIELDS.name, direction: 'asc' };

/** Field lengths, matching the API's. */
const ROLE_NAME_MAX = 60;
const ROLE_DESCRIPTION_MAX = 240;

/** A role flattened for sorting. */
type RoleRow = Role & { readonly permissionCount: number };

/**
 * Settings → Roles: the roles admins define, each a named set of permissions
 * given to people. Adding one names it here, then its page picks the
 * permissions. Shown only while RBAC is switched on, to holders of
 * manage_roles.
 *
 * @returns The tab.
 */
export function RolesTab(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const { search, setSearch, debounced } = useUrlSearch();
  const { sort, onSort } = useTableSort(ROLE_SORT_KEYS, DEFAULT_SORT);
  const roles = useQuery({ queryKey: ROLE_QUERY_KEY.list, queryFn: ({ signal }) => listRoles(signal) });

  const rows: RoleRow[] = roles.data
    ? sortRows(
        filterRows(
          roles.data.map((role) => ({ ...role, permissionCount: role.permissions.length })),
          debounced,
          ['name', 'description'],
        ),
        sort,
      )
    : [];

  return (
    <div className="mt-6 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search roles"
          placeholder="Role or description"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="max-w-xs"
        />
        <span className="ml-auto">
          <Button onClick={() => setAdding(true)}>
            <PlusIcon aria-hidden className="size-4" /> Add role
          </Button>
        </span>
      </div>

      {roles.isError ? (
        <ErrorNotice title="Roles unavailable" message={errorMessage(roles.error)} />
      ) : roles.isPending ? (
        <SkeletonTable label="Loading roles…" />
      ) : rows.length === 0 ? (
        <Panel>
          {debounced ? (
            <EmptyState message="No roles match that search." />
          ) : (
            <EmptyState
              icon={<ShieldCheckIcon aria-hidden className="size-6" />}
              title="No roles yet"
              message="A role is a named set of permissions you give to people, such as Sales or Finance."
              action={<Button onClick={() => setAdding(true)}>Add role</Button>}
            />
          )}
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Role', sortKey: ROLE_SORT_FIELDS.name },
            { label: 'Permissions', sortKey: ROLE_SORT_FIELDS.permissions },
            { label: 'People', sortKey: ROLE_SORT_FIELDS.members },
          ]}
          rightAlignedHeaders={[1, 2]}
          sort={sort}
          onSort={onSort}
        >
          {rows.map((role) => (
            <tr key={role.id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small">
                <Link to={`/settings/roles/${role.id}`} className="font-bold text-brand hover:underline">
                  {role.name}
                </Link>
                {role.system ? (
                  <span className="ml-2">
                    <Pill tone="neutral">Built in</Pill>
                  </span>
                ) : null}
                {role.description ? (
                  <span className="block text-portal-micro text-portal-muted">{role.description}</span>
                ) : null}
              </td>
              <td className="p-3 text-right text-portal-small whitespace-nowrap">{role.permissionCount}</td>
              <td className="p-3 text-right text-portal-small whitespace-nowrap">{role.memberCount}</td>
            </tr>
          ))}
        </DataTable>
      )}

      {adding ? (
        <AddRoleDialog
          onClose={() => setAdding(false)}
          onCreated={async (role) => {
            setAdding(false);
            await queryClient.invalidateQueries({ queryKey: ROLE_QUERY_KEY.list });
            navigate(`/settings/roles/${role.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

/** Names a new role; its page then picks the permissions. */
function AddRoleDialog({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void;
  readonly onCreated: (role: Role) => Promise<void>;
}): React.ReactElement {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const create = useMutation({
    mutationFn: () =>
      createRole({
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        permissions: [],
      }),
    onSuccess: onCreated,
  });
  const valid = name.trim().length > 0;
  return (
    <Dialog open onOpenChange={(next) => (!next && !create.isPending ? onClose() : undefined)}>
      <DialogContent className="sm:max-w-sm">
        <DialogTitle>Add a role</DialogTitle>
        <form
          className="mt-2 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid && !create.isPending) create.mutate();
          }}
        >
          <Field label="Name" htmlFor="new-role-name" hint="What the team calls it, such as Sales or Finance." required>
            <TextInput
              id="new-role-name"
              value={name}
              placeholder="Sales"
              maxLength={ROLE_NAME_MAX}
              autoFocus
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label="Description" htmlFor="new-role-description" hint="Who it's for, in a line.">
            <TextInput
              id="new-role-description"
              value={description}
              maxLength={ROLE_DESCRIPTION_MAX}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>
          <p className="text-portal-micro text-portal-muted">You choose its permissions on the next screen.</p>
          {create.isError ? <ErrorNotice inline title="Not added" message={errorMessage(create.error)} /> : null}
          <div className="flex items-center justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={create.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || create.isPending}>
              {create.isPending ? 'Adding…' : 'Add role'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
